
import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUserStore } from "@/store/userStore";
import { useToast } from "@/hooks/use-toast";

interface AuthGuardProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, wasAutoLoggedOut, setWasAutoLoggedOut } = useUserStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    // Allow access to these public pages without authentication
    const publicPaths = ["/", "/auth", "/enterprise"];
    if (publicPaths.includes(location.pathname)) return;

    if (!isAuthenticated) {
      if (wasAutoLoggedOut) {
        toast({
          title: "Authentication Required",
          description: "Your session has expired. Please log in again.",
          variant: "destructive",
        });
        setWasAutoLoggedOut(false);
      }
      navigate("/auth", { replace: true });
      return;
    }

    if (allowedRoles && !allowedRoles.includes(user?.role)) {
      toast({
        title: "Access Denied",
        description: "You do not have permission to access this page",
        variant: "destructive",
      });
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate, location.pathname, toast, allowedRoles, user, wasAutoLoggedOut]);

  // If on a public page, render children regardless of auth status
  // For all other pages, only render if authenticated
   const publicPaths = ["/", "/auth", "/enterprise"];
  return publicPaths.includes(location.pathname) ||
    (isAuthenticated && (!allowedRoles || allowedRoles.includes(user?.role)))
    ? <>{children}</>
    : null;
};
